import { MontoCRC, MontoUSD } from '@/components/dominio';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useCuentasBancoBC } from '@/hooks/useCuentasBancoBC';
import { useDiariosPagosBC, useEnviarABC, useVistaPreviaEnvioBC } from '@/hooks/usePropuestasPago';
import { ErrorApiCliente } from '@/lib/cliente';
import type { DiarioPagosBC, EnvioBCResultado } from '@adelante/shared';
import { CheckCircle, Warning } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface Props {
  idPropuesta: number;
  /** Cuentas bancarias de origen usadas en la propuesta. */
  cuentasUsadas: string[];
  open: boolean;
  onClose: () => void;
}

const clave = (d: { template: string; name?: string; batch?: string }) =>
  `${d.template}|${d.name ?? d.batch}`;

/** Sugiere el diario según el banco de la cuenta (el nombre del diario trae el banco: "BAC COL"). */
function sugerirDiario(
  diarios: DiarioPagosBC[],
  nombreCuenta: string | undefined,
): DiarioPagosBC | undefined {
  const nombre = (nombreCuenta ?? '').toUpperCase();
  const banco = nombre.includes('BNCR') ? 'BNCR' : nombre.includes('BAC') ? 'BAC' : '';
  return banco ? diarios.find((d) => d.name.toUpperCase().includes(banco)) : undefined;
}

export function EnviarABCDialog({ idPropuesta, cuentasUsadas, open, onClose }: Props) {
  const { data: cuentasBancoData } = useCuentasBancoBC();
  const cuentasBanco = cuentasBancoData?.data ?? [];
  const { data: diariosData } = useDiariosPagosBC(open);
  const diarios = useMemo(() => diariosData?.data ?? [], [diariosData]);

  const [cuenta, setCuenta] = useState(cuentasUsadas[0] ?? '');
  const [diarioClave, setDiarioClave] = useState('');
  const [confirmarConLineas, setConfirmarConLineas] = useState(false);
  const [omitirConCambios, setOmitirConCambios] = useState(false);
  // Fecha elegida a mano (solo cuentas sin archivo); vacía = la que propone el servidor.
  const [fechaManual, setFechaManual] = useState('');
  const [resultado, setResultado] = useState<EnvioBCResultado | null>(null);
  const enviar = useEnviarABC();

  const diario = diarios.find((d) => clave(d) === diarioClave);
  const { data: vistaData, isFetching: cargando } = useVistaPreviaEnvioBC(
    idPropuesta,
    cuenta,
    diario ? { template: diario.template, batch: diario.name } : undefined,
    open && !resultado,
    fechaManual || undefined,
  );
  const vista = vistaData?.data ?? null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo resincronizar cuando cambia la lista o se abre
  useEffect(() => {
    if (open && !cuentasUsadas.includes(cuenta)) setCuenta(cuentasUsadas[0] ?? '');
  }, [open, cuentasUsadas]);

  // Al cambiar de cuenta se vuelve a elegir diario y se limpia la confirmación.
  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reaccionar al cambio de cuenta
  useEffect(() => {
    setDiarioClave('');
    setConfirmarConLineas(false);
    setOmitirConCambios(false);
    setFechaManual('');
  }, [cuenta]);

  // Diario por defecto: el último usado con esta cuenta; si no, el que coincide con el banco.
  useEffect(() => {
    if (!open || diarioClave || diarios.length === 0 || !vista) return;
    const ultimo = vista.ultimoDiario
      ? diarios.find(
          (d) =>
            d.template === vista.ultimoDiario?.template && d.name === vista.ultimoDiario?.batch,
        )
      : undefined;
    const nombreCuenta = cuentasBanco.find((c) => c.number === cuenta)?.displayName;
    const elegido = ultimo ?? sugerirDiario(diarios, nombreCuenta);
    if (elegido) setDiarioClave(clave(elegido));
  }, [open, diarioClave, diarios, vista, cuenta, cuentasBanco]);

  function cerrar() {
    setResultado(null);
    setConfirmarConLineas(false);
    setOmitirConCambios(false);
    onClose();
  }

  function enviarAhora() {
    if (!diario || !vista || vista.bloqueos.length > 0) return;
    enviar.mutate(
      {
        id: idPropuesta,
        cuentaBancoOrigen: cuenta,
        journalTemplate: diario.template,
        journalBatch: diario.name,
        confirmarDiarioConLineas: confirmarConLineas,
        omitirProveedoresConCambios: omitirConCambios,
        postingDate: vista.modo === 'SIN_ARCHIVO' ? vista.postingDate || undefined : undefined,
      },
      {
        onSuccess: (r) => {
          if (r.data) setResultado(r.data);
          else toast.error(r.error?.mensaje ?? 'No se pudo enviar a BC');
        },
        onError: (e) => {
          if (e instanceof ErrorApiCliente && e.codigo === 'DIARIO_CON_LINEAS') {
            // El servidor encontró líneas ajenas en el diario: pedir confirmación explícita.
            setConfirmarConLineas(false);
            toast.warning(e.message);
          } else {
            toast.error(e instanceof Error ? e.message : 'No se pudo enviar a BC');
          }
        },
      },
    );
  }

  const Monto = vista?.currencyCode === 'USD' ? MontoUSD : MontoCRC;
  const lineasEnDiario = vista?.lineasEnDiario ?? 0;
  const necesitaConfirmar = lineasEnDiario > 0;
  // Proveedores con facturas que en BC ya se cerraron o perdieron saldo: solo se envía si se omiten.
  const cambios = vista?.proveedoresConCambios ?? [];
  const proveedoresConCambios = [...new Set(cambios.map((c) => c.vendorCode))];
  const todosConCambios =
    !!vista && vista.pagos.length > 0 && proveedoresConCambios.length >= vista.pagos.length;
  const puedeEnviar =
    !!diario &&
    !!vista &&
    vista.bloqueos.length === 0 &&
    vista.pagos.length > 0 &&
    (cambios.length === 0 || (omitirConCambios && !todosConCambios)) &&
    (!necesitaConfirmar || confirmarConLineas) &&
    !enviar.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Enviar a Business Central</DialogTitle>
          <DialogDescription>
            Deja un pago por proveedor en el Diario de pagos de BC, con las facturas de esta
            propuesta marcadas para liquidar. No registra nada: Contabilidad revisa el diario y lo
            registra.
          </DialogDescription>
        </DialogHeader>

        {resultado ? (
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-2 rounded-md border border-adelante-primary/30 bg-adelante-primary/5 p-3">
              <CheckCircle
                className="mt-0.5 h-5 w-5 shrink-0 text-adelante-primary-dark"
                weight="bold"
                aria-hidden="true"
              />
              <div className="space-y-1">
                <p className="font-medium">
                  {resultado.totalPagos} pago(s) quedaron en el diario "{resultado.journalBatch}" de
                  BC.
                </p>
                <p className="text-muted-foreground">
                  Sin registrar. En BC: Diarios de pagos → sección {resultado.journalBatch}.
                  {resultado.lineasReemplazadas > 0 &&
                    ` Se reemplazaron ${resultado.lineasReemplazadas} línea(s) de un envío anterior.`}
                </p>
              </div>
            </div>
            {resultado.omitidos.length > 0 && (
              <div className="space-y-1 rounded-md border border-amber-400/60 bg-amber-50 p-3 text-xs">
                <p className="font-medium">
                  {resultado.omitidos.length} proveedor(es) NO se enviaron: hay que registrarlos a
                  mano en BC.
                </p>
                <ul className="list-inside list-disc">
                  {resultado.omitidos.map((o) => (
                    <li key={o.vendorCode}>
                      {o.vendorName ?? o.vendorCode}
                      {o.consecutivo !== null && ` (consecutivo ${o.consecutivo})`} — {o.motivo}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={cerrar}>Cerrar</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="abc-cuenta">Cuenta bancaria de origen</Label>
                <select
                  id="abc-cuenta"
                  value={cuenta}
                  onChange={(e) => setCuenta(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {cuentasUsadas.map((numero) => {
                    const c = cuentasBanco.find((x) => x.number === numero);
                    return (
                      <option key={numero} value={numero}>
                        {c ? `${c.displayName} (${c.number})` : numero}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="abc-diario">Diario de pagos en BC</Label>
                <select
                  id="abc-diario"
                  value={diarioClave}
                  onChange={(e) => {
                    setDiarioClave(e.target.value);
                    setConfirmarConLineas(false);
                  }}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="">Elegir diario…</option>
                  {diarios.map((d) => (
                    <option key={clave(d)} value={clave(d)}>
                      {d.name}
                      {d.description ? ` — ${d.description.trim()}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {cargando && !vista && <p className="text-sm text-muted-foreground">Revisando…</p>}

            {vista && vista.bloqueos.length > 0 && (
              <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <p className="flex items-center gap-2 font-medium text-destructive">
                  <Warning className="h-4 w-4" weight="bold" aria-hidden="true" />
                  No se puede enviar todavía
                </p>
                <ul className="list-inside list-disc text-xs">
                  {vista.bloqueos.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              </div>
            )}

            {cambios.length > 0 && (
              <div className="space-y-2 rounded-md border border-amber-400/60 bg-amber-50 p-3 text-xs">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <Warning className="h-4 w-4" weight="bold" aria-hidden="true" />
                  {proveedoresConCambios.length} proveedor(es) con facturas que cambiaron en BC
                </p>
                <ul className="list-inside list-disc">
                  {cambios.map((c) => (
                    <li key={c.entryNo}>
                      {c.documentNo ?? c.entryNo} ({c.vendorName ?? c.vendorCode}
                      {c.consecutivo !== null && `, consecutivo ${c.consecutivo}`}): {c.motivo}
                    </li>
                  ))}
                </ul>
                {todosConCambios ? (
                  <p>Todos los proveedores tienen cambios: no hay nada que enviar.</p>
                ) : (
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={omitirConCambios}
                      onChange={(e) => setOmitirConCambios(e.target.checked)}
                    />
                    <span>
                      Enviar sin esos proveedores. Contabilidad los registra a mano en BC; el resto
                      conserva su consecutivo del archivo de banco.
                    </span>
                  </label>
                )}
              </div>
            )}

            {vista?.envioPrevio && (
              <p className="rounded-md border border-border bg-muted/30 p-2 text-xs text-muted-foreground">
                {vista.envioPrevio.estado === 'ENVIADO'
                  ? `Ya se envió el ${new Date(vista.envioPrevio.enviadoEn).toLocaleString('es-CR', { dateStyle: 'short', timeStyle: 'short' })} al diario "${vista.envioPrevio.journalBatch}" (${vista.envioPrevio.totalPagos} pagos). Si enviás de nuevo, esas líneas se reemplazan.`
                  : `El último intento falló: ${vista.envioPrevio.ultimoError ?? 'sin detalle'}. Las líneas anteriores ya no están en BC.`}
              </p>
            )}

            {vista?.modo === 'SIN_ARCHIVO' && (
              <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3 text-xs">
                <p>
                  Esta cuenta no genera archivo de banco, así que los pagos van{' '}
                  <strong>sin consecutivo</strong>: el N.º de documento lo asigna la app
                  (correlativo después del último número del diario) y el{' '}
                  <strong>Documento externo queda en blanco</strong> para que lo llene Contabilidad.
                </p>
                <div className="flex items-center gap-2">
                  <Label htmlFor="abc-fecha" className="text-xs">
                    Fecha de registro
                  </Label>
                  <input
                    id="abc-fecha"
                    type="date"
                    value={fechaManual || vista.postingDate || ''}
                    onChange={(e) => setFechaManual(e.target.value)}
                    className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                  />
                </div>
              </div>
            )}

            {vista && vista.pagos.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-baseline justify-between text-sm">
                  <span>
                    <strong>{vista.pagos.length}</strong> pago(s) · fecha de registro{' '}
                    <strong>{vista.postingDate}</strong>
                    {vista.envioNo !== null && vista.modo === 'ARCHIVO' && (
                      <> · envío {vista.envioNo}</>
                    )}
                  </span>
                  <span className="font-mono font-semibold">
                    <Monto monto={vista.total} />
                  </span>
                </div>
                <div className="max-h-64 overflow-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-muted/60 text-left">
                      <tr>
                        <th className="px-2 py-1 font-medium">Proveedor</th>
                        <th className="px-2 py-1 font-medium">Consecutivo</th>
                        <th className="px-2 py-1 text-right font-medium">Facturas</th>
                        <th className="px-2 py-1 text-right font-medium">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vista.pagos.map((p) => (
                        <tr key={p.vendorCode} className="border-t border-border/50">
                          <td className="px-2 py-1">
                            {p.vendorName ?? p.vendorCode}{' '}
                            <span className="font-mono text-muted-foreground">{p.vendorCode}</span>
                          </td>
                          <td className="px-2 py-1 font-mono">{p.consecutivo ?? '—'}</td>
                          <td
                            className="px-2 py-1 text-right"
                            title={p.facturas.map((f) => f.documentNo).join(', ')}
                          >
                            {p.facturas.length}
                          </td>
                          <td className="px-2 py-1 text-right font-mono">
                            <Monto monto={p.total} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {vista && vista.excluidos.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Fuera del diario (no entraron al archivo de banco):{' '}
                {vista.excluidos.map((e) => e.vendorName ?? e.vendorCode).join(', ')}.
              </p>
            )}

            {vista && necesitaConfirmar && vista.bloqueos.length === 0 && (
              <label className="flex items-start gap-2 rounded-md border border-amber-400/60 bg-amber-50 p-3 text-xs">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={confirmarConLineas}
                  onChange={(e) => setConfirmarConLineas(e.target.checked)}
                />
                <span>
                  El diario "{diario?.name}" ya tiene <strong>{lineasEnDiario}</strong> línea(s) en
                  BC. Si son de Contabilidad para esta misma corrida de pagos, enviar de nuevo
                  duplicaría los pagos. Marcá esta casilla solo si querés agregarlas de todos modos.
                </span>
              </label>
            )}

            <div className="flex min-w-0 flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={cerrar}>
                Cancelar
              </Button>
              <Button onClick={enviarAhora} disabled={!puedeEnviar}>
                {enviar.isPending ? 'Enviando…' : 'Enviar al diario'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
